/**
 * langflow-collage component
 *
 * Renders an interactive collage of 7 Langflow UI node cards in a 3-column
 * mosaic layout. Matches the visual in craft-01.png.
 * Desktop only. Returns null on mobile/touch devices.
 *
 * Interactive features:
 *   - Functional <select>, <input>, <textarea>, <input type="range">
 *   - Hover states on icon buttons and tool rows
 *   - Shimmer animation on chat input labels
 *   - Live range value display update
 *   - History card mode toggle (Retrieve / Store)
 *
 * @param {object} props - (unused, no props required)
 * @param {number} index - Panel index for stagger animation
 * @returns {HTMLElement|null}
 */

import './langflow-collage.css';
import { PANEL_ANIMATION_DELAY_MS } from '../constants.js';

const DESKTOP_QUERY = '(pointer: fine) and (min-width: 769px)';

export function createLangflowCollage(props, index) {
  const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
  if (!isDesktop) return null;

  const panel = document.createElement('div');
  panel.className = 'portfolio-panel portfolio-panel--component';
  // No images to load — remove skeleton immediately
  panel.classList.remove('skeleton-loading');

  buildCollage(panel);
  setTimeout(
    () => panel.classList.add('animated'),
    PANEL_ANIMATION_DELAY_MS * (index || 0),
  );
  return panel;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Registry of all open-close functions for select dropdowns — opening one closes all others
const _selectClosers = new Set();

function el(tag, className, text) {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

function formRow(labelText, inputEl) {
  const row = el('div', 'lfc-row');
  row.appendChild(el('span', 'lfc-label', labelText));
  row.appendChild(inputEl);
  return row;
}

function makeSelect(options) {
  const wrapper = el('div', 'lfc-select-wrap');

  const trigger = el('button', 'lfc-select');
  trigger.type = 'button';
  trigger.dataset.value = options[0][0];

  // Icon slot in trigger (only shown when options have icons)
  const triggerIcon = el('span', 'lfc-select-icon');
  if (options[0][2]) triggerIcon.innerHTML = options[0][2];
  trigger.appendChild(triggerIcon);

  const labelSpan = el('span', 'lfc-select-text', options[0][1]);
  trigger.appendChild(labelSpan);

  const chevron = document.createElement('span');
  chevron.className = 'lfc-select-chevron';
  chevron.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M7 12.25L3.938 9.188L4.554 8.571L7 11.012L9.446 8.571L10.063 9.188L7 12.25Z" fill="#F8F8F8"/>' +
    '<path d="M7 1.75L10.063 4.813L9.446 5.429L7 2.988L4.554 5.429L3.938 4.813L7 1.75Z" fill="#F8F8F8"/>' +
    '</svg>';
  trigger.appendChild(chevron);

  const list = el('ul', 'lfc-select-list');
  list.setAttribute('role', 'listbox');

  options.forEach(([value, text, iconSvg], i) => {
    const item = el('li', 'lfc-select-item' + (i === 0 ? ' selected' : ''));
    item.setAttribute('role', 'option');
    item.dataset.value = value;
    if (iconSvg) {
      const iconWrap = el('span', 'lfc-select-item-icon');
      iconWrap.innerHTML = iconSvg;
      item.appendChild(iconWrap);
    }
    item.appendChild(el('span', null, text));
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      triggerIcon.innerHTML = iconSvg || '';
      labelSpan.textContent = text;
      trigger.dataset.value = value;
      list
        .querySelectorAll('.lfc-select-item')
        .forEach((el) => el.classList.remove('selected'));
      item.classList.add('selected');
      close();
    });
    list.appendChild(item);
  });

  wrapper.appendChild(trigger);
  wrapper.appendChild(list);

  function open() {
    // Close every other open select first
    _selectClosers.forEach((fn) => fn !== close && fn());
    list.classList.add('open');
    trigger.classList.add('open');
    _selectClosers.add(close);
    setTimeout(() => document.addEventListener('click', onOutside), 0);
  }

  function close() {
    list.classList.remove('open');
    trigger.classList.remove('open');
    _selectClosers.delete(close);
    document.removeEventListener('click', onOutside);
  }

  function onOutside(e) {
    if (!wrapper.contains(e.target)) close();
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    list.classList.contains('open') ? close() : open();
  });

  return wrapper;
}

function makeInput(placeholder) {
  const input = el('input', 'lfc-input');
  input.type = 'text';
  input.placeholder = placeholder;
  return input;
}

function makeNumberInput(value, unit) {
  const wrap = el('div', 'lfc-input-suffix-wrap');
  const input = el('input', 'lfc-input-number-bare');
  input.type = 'number';
  input.value = value;
  input.min = '0';
  const suffix = el('span', 'lfc-input-suffix-label', unit);
  wrap.appendChild(input);
  wrap.appendChild(suffix);
  return wrap;
}

function rangeRow(labelText, min, max, value) {
  const row = el('div', 'lfc-row');
  row.appendChild(el('span', 'lfc-label', labelText));

  const minN = Number(min);
  const maxN = Number(max);
  let curN = Number(value);

  const track = el('div', 'lfc-range-track');
  const fill = el('div', 'lfc-range-fill');
  const thumb = el('div', 'lfc-range-thumb');
  track.appendChild(fill);
  track.appendChild(thumb);

  const valueSpan = el('span', 'lfc-range-value', value);

  function updatePos() {
    const pct = ((curN - minN) / (maxN - minN)) * 100;
    fill.style.width = pct + '%';
    thumb.style.left = pct + '%';
  }
  updatePos();

  // Drag handling
  function onMove(clientX) {
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    curN = Math.round(minN + pct * (maxN - minN));
    valueSpan.textContent = curN;
    updatePos();
  }

  track.addEventListener('mousedown', (e) => {
    e.preventDefault();
    onMove(e.clientX);
    function onMouseMove(e) {
      onMove(e.clientX);
    }
    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });

  row.appendChild(track);
  row.appendChild(valueSpan);
  return row;
}

// ---------------------------------------------------------------------------
// Card builders
// ---------------------------------------------------------------------------

// Embedding model provider icons (12×12, from Paper)
const SVG_ICON_LANGFLOW =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<defs><clipPath id="lfc-lf"><rect width="12" height="12" fill="#fff"/></clipPath></defs>' +
  '<g clip-path="url(#lfc-lf)">' +
  '<path d="M4.644 4.368V3.228C4.644 3.132 4.68 3.06 4.764 3.012L7.056 1.692C7.368 1.512 7.74 1.428 8.124 1.428C9.564 1.428 10.476 2.544 10.476 3.732C10.476 3.816 10.476 3.912 10.464 4.008L8.088 2.616C7.944 2.532 7.8 2.532 7.656 2.616L4.644 4.368ZM9.996 8.808V6.084C9.996 5.916 9.924 5.796 9.78 5.712L6.768 3.96L7.752 3.396C7.836 3.348 7.908 3.348 7.992 3.396L10.284 4.716C10.944 5.1 11.388 5.916 11.388 6.708C11.388 7.62 10.848 8.46 9.996 8.808ZM3.936 6.408L2.952 5.832C2.868 5.784 2.832 5.712 2.832 5.616V2.976C2.832 1.692 3.816 0.72 5.148 0.72C5.652 0.72 6.12 0.888 6.516 1.188L4.152 2.556C4.008 2.64 3.936 2.76 3.936 2.928V6.408ZM6.054 7.632L4.644 6.84V5.16L6.054 4.368L7.464 5.16V6.84L6.054 7.632ZM6.96 11.28C6.456 11.28 5.988 11.112 5.592 10.812L7.956 9.444C8.1 9.36 8.172 9.24 8.172 9.072V5.592L9.168 6.168C9.252 6.216 9.288 6.288 9.288 6.384V9.024C9.288 10.308 8.292 11.28 6.96 11.28ZM4.116 8.604L1.824 7.284C1.164 6.9 0.72 6.084 0.72 5.292C0.72 4.368 1.272 3.54 2.124 3.192V5.928C2.124 6.096 2.196 6.216 2.34 6.3L5.34 8.04L4.356 8.604C4.272 8.652 4.2 8.652 4.116 8.604ZM3.984 10.572C2.628 10.572 1.632 9.552 1.632 8.292C1.632 8.196 1.644 8.1 1.656 8.004L4.02 9.372C4.164 9.456 4.308 9.456 4.452 9.372L7.464 7.632V8.772C7.464 8.868 7.428 8.94 7.344 8.988L5.052 10.308C4.74 10.488 4.368 10.572 3.984 10.572ZM6.96 12C8.412 12 9.624 10.968 9.9 9.6C11.244 9.252 12.108 7.992 12.108 6.708C12.108 5.868 11.748 5.052 11.1 4.464C11.16 4.212 11.196 3.96 11.196 3.708C11.196 1.992 9.804 0.708 8.196 0.708C7.872 0.708 7.56 0.756 7.248 0.864C6.708 0.336 5.964 0 5.148 0C3.696 0 2.484 1.032 2.208 2.4C0.864 2.748 0 4.008 0 5.292C0 6.132 0.36 6.948 1.008 7.536C0.948 7.788 0.912 8.04 0.912 8.292C0.912 10.008 2.304 11.292 3.912 11.292C4.236 11.292 4.548 11.244 4.86 11.136C5.4 11.664 6.144 12 6.96 12Z" fill="currentColor"/>' +
  '</g></svg>';

const SVG_ICON_GOOGLE =
  '<svg width="12" height="12" viewBox="0 0 753 768" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M752.64 392.727C752.64 365.498 750.196 339.316 745.658 314.182H384V462.895H590.662C581.585 510.72 554.356 551.215 513.513 578.444V675.142H638.138C710.749 608.116 752.64 509.673 752.64 392.727Z" fill="#4285F4"/>' +
  '<path d="M384 768C487.68 768 574.604 733.789 638.138 675.142L513.513 578.444C479.302 601.484 435.665 615.447 384 615.447C284.16 615.447 199.331 548.073 168.96 457.309H41.193V556.451C104.378 681.775 233.891 768 384 768Z" fill="#34A853"/>' +
  '<path d="M168.96 456.96C161.28 433.92 156.742 409.484 156.742 384C156.742 358.516 161.28 334.08 168.96 311.04V211.898H41.193C15.011 263.564 0 321.862 0 384C0 446.138 15.011 504.436 41.193 556.102L168.96 456.96Z" fill="#FBBC05"/>' +
  '<path d="M384 152.902C440.553 152.902 490.822 172.451 530.967 210.153L640.931 100.189C574.255 38.051 487.68 0 384 0C233.891 0 104.378 86.225 41.193 211.898L168.96 311.04C199.331 220.276 284.16 152.902 384 152.902Z" fill="#EA4335"/>' +
  '</svg>';

const SVG_ICON_COHERE =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M3.888 7.152C4.208 7.152 4.848 7.136 5.744 6.768C6.784 6.336 8.832 5.568 10.32 4.768C11.36 4.208 11.808 3.472 11.808 2.48C11.808 1.12 10.704 0 9.328 0H3.568C1.6 0 0 1.6 0 3.568C0 5.536 1.504 7.152 3.888 7.152Z" fill="#39594D"/>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M4.864 9.6C4.864 8.64 5.44 7.76 6.336 7.392L8.144 6.64C9.984 5.888 12 7.232 12 9.216C12 10.752 10.752 12 9.216 12H7.248C5.936 12 4.864 10.928 4.864 9.6Z" fill="#D18EE2"/>' +
  '<path d="M2.064 7.616C0.928 7.616 0 8.544 0 9.68V9.952C0 11.072 0.928 12 2.064 12C3.2 12 4.128 11.072 4.128 9.936V9.664C4.112 8.544 3.2 7.616 2.064 7.616Z" fill="#FF7759"/>' +
  '</svg>';

// Shared SVG icon helpers for header buttons
const SVG_CODE =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M10.667 12L14.667 8L10.667 4M5.333 4L1.333 8L5.333 12" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const SVG_DOTS =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M8 8.667C8.368 8.667 8.667 8.368 8.667 8C8.667 7.632 8.368 7.333 8 7.333C7.632 7.333 7.333 7.632 7.333 8C7.333 8.368 7.632 8.667 8 8.667Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M8 4C8.368 4 8.667 3.701 8.667 3.333C8.667 2.965 8.368 2.667 8 2.667C7.632 2.667 7.333 2.965 7.333 3.333C7.333 3.701 7.632 4 8 4Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M8 13.333C8.368 13.333 8.667 13.035 8.667 12.667C8.667 12.298 8.368 12 8 12C7.632 12 7.333 12.298 7.333 12.667C7.333 13.035 7.632 13.333 8 13.333Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const SVG_PLUS =
  '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M3.333 8H12.667M8 3.333V12.667" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

function cardIconWrap(svgContent) {
  const wrap = el('div', 'lfc-card-icon-wrap');
  wrap.innerHTML = svgContent;
  return wrap;
}

function cardHeaderFull(iconSvg, title) {
  const header = el('div', 'lfc-card-header');
  header.appendChild(cardIconWrap(iconSvg));
  header.appendChild(el('div', 'lfc-card-title', title));
  const actions = el('div', 'lfc-card-header-actions');
  const codeBtn = el('button', 'lfc-btn-icon');
  codeBtn.type = 'button';
  codeBtn.innerHTML = SVG_CODE;
  const dotsBtn = el('button', 'lfc-btn-icon');
  dotsBtn.type = 'button';
  dotsBtn.innerHTML = SVG_DOTS;
  actions.appendChild(codeBtn);
  actions.appendChild(dotsBtn);
  header.appendChild(actions);
  return header;
}

function buildAgentCard() {
  const card = el('div', 'lfc-card lfc-card--agent');

  // Header: robot icon (antenna + body SVG) in 32px #262626 square
  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M12.778 10.938V12.813M7.222 10.938V12.813M10 5.313V2.5M10 5.313C5.398 5.313 1.667 8.46 1.667 12.344C1.667 16.227 5.398 17.5 10 17.5C14.602 17.5 18.333 16.227 18.333 12.344C18.333 8.46 14.602 5.313 10 5.313Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>',
      'Agent',
    ),
  );

  // Model row — cycles through AI models
  const modelRow = el('div', 'lfc-row');
  modelRow.appendChild(el('span', 'lfc-label', 'Model'));

  const modelValue = el('div', 'lfc-model-value');

  function setModel(m) {
    modelValue.innerHTML = m.iconSvg + '<span>' + m.name + '</span>';
  }
  setModel(MODELS[0]);

  const slidersBtn = el('button', 'lfc-btn-icon');
  slidersBtn.type = 'button';
  // Two-circle sliders icon from Paper
  slidersBtn.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M13.333 4.667H7.333M9.333 11.333H3.333M9.333 11.333C9.333 12.438 10.229 13.333 11.333 13.333C12.438 13.333 13.333 12.438 13.333 11.333C13.333 10.229 12.438 9.333 11.333 9.333C10.229 9.333 9.333 10.229 9.333 11.333ZM6.667 4.667C6.667 5.771 5.771 6.667 4.667 6.667C3.562 6.667 2.667 5.771 2.667 4.667C2.667 3.562 3.562 2.667 4.667 2.667C5.771 2.667 6.667 3.562 6.667 4.667Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  modelRow.appendChild(modelValue);
  modelRow.appendChild(slidersBtn);
  card.appendChild(modelRow);

  // Cycle through models — offset 2 s to stagger from chat prompt cycling
  let modelIdx = 0;
  const MODEL_EXIT_MS = 300;
  const MODEL_ENTER_MS = 300;
  const MODEL_HOLD_MS = 5000;
  const MODEL_TRANSITION = `opacity ${MODEL_EXIT_MS}ms ease, transform ${MODEL_EXIT_MS}ms ease`;

  setTimeout(() => {
    setInterval(() => {
      modelValue.style.transition = MODEL_TRANSITION;
      modelValue.style.opacity = '0';
      modelValue.style.transform = 'translateY(-10px)';

      setTimeout(() => {
        modelIdx = (modelIdx + 1) % MODELS.length;
        setModel(MODELS[modelIdx]);
        modelValue.style.transition = 'none';
        modelValue.style.opacity = '0';
        modelValue.style.transform = 'translateY(10px)';

        void modelValue.offsetHeight;

        modelValue.style.transition = `opacity ${MODEL_ENTER_MS}ms ease, transform ${MODEL_ENTER_MS}ms ease`;
        modelValue.style.opacity = '1';
        modelValue.style.transform = 'translateY(0)';
      }, MODEL_EXIT_MS);
    }, MODEL_HOLD_MS + MODEL_EXIT_MS);
  }, 2000);

  // Instructions section header
  const instrRow = el('div', 'lfc-section-header');
  instrRow.appendChild(el('span', null, 'Instructions'));
  const instrIconBtn = el('button', 'lfc-btn-icon');
  instrIconBtn.type = 'button';
  instrIconBtn.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M2.167 11.333H3.833L7.167 8H8.833L12.167 4.667H13.833M7.167 13H8.833L12.167 9.667H13.833M2.167 6.333H3.833L7.167 3H8.833" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  instrRow.appendChild(instrIconBtn);
  card.appendChild(instrRow);

  const instrBox = el('textarea', 'lfc-textarea');
  instrBox.value =
    'You are a helpful assistant that summarizes user feedback into clear insights and next steps. Be concise, empathetic, and structured. Include actionable recommendations.';
  card.appendChild(instrBox);

  // Data section
  const dataHeader = el('div', 'lfc-section-header');
  dataHeader.appendChild(el('span', null, 'Data'));
  const dataAddBtn = el('button', 'lfc-btn-icon');
  dataAddBtn.type = 'button';
  dataAddBtn.innerHTML = SVG_PLUS;
  dataHeader.appendChild(dataAddBtn);
  card.appendChild(dataHeader);
  card.appendChild(
    el(
      'p',
      'lfc-muted-body',
      'Add data to your agent to get better responses.',
    ),
  );

  // Tools section
  const toolsHeader = el('div', 'lfc-section-header');
  toolsHeader.appendChild(el('span', null, 'Tools'));
  const toolsAddBtn = el('button', 'lfc-btn-icon');
  toolsAddBtn.type = 'button';
  toolsAddBtn.innerHTML = SVG_PLUS;
  toolsHeader.appendChild(toolsAddBtn);
  card.appendChild(toolsHeader);

  // Discord tool row — full Discord SVG logo
  const discordRow = el('div', 'lfc-tool-row');
  const discordIconEl = el('span', 'lfc-tool-icon-svg');
  discordIconEl.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<defs><clipPath id="dc1"><rect width="18" height="18" fill="#fff"/></clipPath></defs>' +
    '<g clip-path="url(#dc1)">' +
    '<path d="M15.238 3.117C14.091 2.59 12.86 2.202 11.574 1.98C11.55 1.976 11.527 1.987 11.515 2.008C11.357 2.29 11.181 2.657 11.059 2.945C9.675 2.738 8.299 2.738 6.944 2.945C6.821 2.65 6.639 2.29 6.48 2.008C6.468 1.987 6.445 1.977 6.422 1.98C5.136 2.202 3.906 2.59 2.758 3.117C2.748 3.121 2.739 3.128 2.734 3.137C0.4 6.624 -0.239 10.024 0.074 13.383C0.076 13.399 0.085 13.415 0.098 13.425C1.637 14.556 3.129 15.242 4.592 15.697C4.616 15.704 4.641 15.695 4.656 15.676C5.002 15.203 5.31 14.705 5.575 14.181C5.591 14.15 5.576 14.113 5.544 14.101C5.054 13.915 4.588 13.689 4.14 13.432C4.104 13.411 4.101 13.361 4.134 13.336C4.228 13.265 4.323 13.192 4.413 13.118C4.429 13.104 4.452 13.101 4.471 13.11C7.417 14.455 10.606 14.455 13.517 13.11C13.536 13.101 13.559 13.103 13.576 13.117C13.666 13.191 13.761 13.265 13.856 13.336C13.888 13.361 13.886 13.411 13.851 13.432C13.402 13.694 12.936 13.915 12.446 14.101C12.414 14.113 12.4 14.15 12.415 14.181C12.686 14.704 12.994 15.203 13.334 15.675C13.348 15.695 13.374 15.704 13.397 15.697C14.868 15.242 16.36 14.556 17.899 13.425C17.913 13.415 17.921 13.4 17.922 13.383C18.298 9.501 17.294 6.128 15.261 3.138C15.256 3.128 15.248 3.121 15.238 3.117ZM6.015 11.338C5.128 11.338 4.397 10.524 4.397 9.524C4.397 8.524 5.114 7.709 6.015 7.709C6.923 7.709 7.647 8.531 7.633 9.524C7.633 10.524 6.916 11.338 6.015 11.338ZM11.996 11.338C11.109 11.338 10.378 10.524 10.378 9.524C10.378 8.524 11.095 7.709 11.996 7.709C12.904 7.709 13.628 8.531 13.614 9.524C13.614 10.524 12.904 11.338 11.996 11.338Z" fill="#5765F2"/>' +
    '</g></svg>';
  discordRow.appendChild(discordIconEl);
  discordRow.appendChild(el('span', 'lfc-tool-name', 'Discord'));
  discordRow.appendChild(el('span', 'lfc-tool-actions', '25 actions'));
  card.appendChild(discordRow);

  // GitHub tool row — full GitHub SVG logo
  const githubRow = el('div', 'lfc-tool-row');
  const githubIconEl = el('span', 'lfc-tool-icon-svg');
  githubIconEl.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M9 0C4.027 0 0 4.027 0 9C0 12.982 2.576 16.346 6.154 17.539C6.604 17.617 6.772 17.348 6.772 17.111C6.772 16.898 6.761 16.189 6.761 15.435C4.5 15.851 3.915 14.884 3.735 14.377C3.634 14.119 3.195 13.32 2.813 13.106C2.498 12.938 2.047 12.521 2.801 12.51C3.51 12.499 4.016 13.162 4.185 13.432C4.995 14.794 6.289 14.411 6.806 14.175C6.885 13.59 7.121 13.196 7.38 12.971C5.378 12.746 3.285 11.97 3.285 8.527C3.285 7.549 3.634 6.739 4.207 6.109C4.117 5.884 3.803 4.961 4.298 3.724C4.298 3.724 5.051 3.487 6.772 4.646C7.492 4.444 8.258 4.343 9.023 4.343C9.787 4.343 10.553 4.444 11.273 4.646C12.994 3.476 13.748 3.724 13.748 3.724C14.242 4.961 13.928 5.884 13.838 6.109C14.411 6.739 14.76 7.537 14.76 8.527C14.76 11.981 12.656 12.746 10.654 12.971C10.98 13.252 11.261 13.793 11.261 14.636C11.261 15.84 11.25 16.808 11.25 17.111C11.25 17.348 11.419 17.629 11.869 17.539C15.424 16.346 18 12.971 18 9C18 4.027 13.973 0 9 0Z" fill="currentColor"/>' +
    '</svg>';
  githubRow.appendChild(githubIconEl);
  githubRow.appendChild(el('span', 'lfc-tool-name', 'GitHub'));
  githubRow.appendChild(el('span', 'lfc-tool-actions', '2 actions'));
  card.appendChild(githubRow);

  return card;
}

const MODELS = [
  {
    name: 'Claude Sonnet 4.6',
    iconSvg:
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">' +
      '<rect width="18" height="18" rx="4.5" fill="#D97757"/>' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.168 5L11.453 13.239H9.616L8.944 11.51H5.508L4.837 13.239H3L6.284 5H8.168ZM11.715 5L15 13.239H13.198L9.913 5H11.715ZM6.103 9.979H8.351L7.227 7.083L6.103 9.979Z" fill="#000000"/>' +
      '</svg>',
  },
  {
    name: 'GPT-5.3-Codex',
    iconSvg:
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">' +
      '<rect width="18" height="18" rx="4.5" style="fill: var(--lfc-send-btn-bg)"/>' +
      '<svg x="3.5" y="3.5" width="11" height="11" viewBox="0 0 12 12">' +
      '<defs><clipPath id="lfc-m-gpt"><rect width="12" height="12" fill="#fff"/></clipPath></defs>' +
      '<g clip-path="url(#lfc-m-gpt)">' +
      '<path d="M4.644 4.368V3.228C4.644 3.132 4.68 3.06 4.764 3.012L7.056 1.692C7.368 1.512 7.74 1.428 8.124 1.428C9.564 1.428 10.476 2.544 10.476 3.732C10.476 3.816 10.476 3.912 10.464 4.008L8.088 2.616C7.944 2.532 7.8 2.532 7.656 2.616L4.644 4.368ZM9.996 8.808V6.084C9.996 5.916 9.924 5.796 9.78 5.712L6.768 3.96L7.752 3.396C7.836 3.348 7.908 3.348 7.992 3.396L10.284 4.716C10.944 5.1 11.388 5.916 11.388 6.708C11.388 7.62 10.848 8.46 9.996 8.808V8.808ZM3.936 6.408L2.952 5.832C2.868 5.784 2.832 5.712 2.832 5.616V2.976C2.832 1.692 3.816 0.72 5.148 0.72C5.652 0.72 6.12 0.888 6.516 1.188L4.152 2.556C4.008 2.64 3.936 2.76 3.936 2.928V6.408L3.936 6.408ZM6.054 7.632L4.644 6.84V5.16L6.054 4.368L7.464 5.16V6.84L6.054 7.632ZM6.96 11.28C6.456 11.28 5.988 11.112 5.592 10.812L7.956 9.444C8.1 9.36 8.172 9.24 8.172 9.072V5.592L9.168 6.168C9.252 6.216 9.288 6.288 9.288 6.384V9.024C9.288 10.308 8.292 11.28 6.96 11.28V11.28ZM4.116 8.604L1.824 7.284C1.164 6.9 0.72 6.084 0.72 5.292C0.72 4.368 1.272 3.54 2.124 3.192V5.928C2.124 6.096 2.196 6.216 2.34 6.3L5.34 8.04L4.356 8.604C4.272 8.652 4.2 8.652 4.116 8.604ZM3.984 10.572C2.628 10.572 1.632 9.552 1.632 8.292C1.632 8.196 1.644 8.1 1.656 8.004L4.02 9.372C4.164 9.456 4.308 9.456 4.452 9.372L7.464 7.632V8.772C7.464 8.868 7.428 8.94 7.344 8.988L5.052 10.308C4.74 10.488 4.368 10.572 3.984 10.572H3.984ZM6.96 12C8.412 12 9.624 10.968 9.9 9.6C11.244 9.252 12.108 7.992 12.108 6.708C12.108 5.868 11.748 5.052 11.1 4.464C11.16 4.212 11.196 3.96 11.196 3.708C11.196 1.992 9.804 0.708 8.196 0.708C7.872 0.708 7.56 0.756 7.248 0.864C6.708 0.336 5.964 0 5.148 0C3.696 0 2.484 1.032 2.208 2.4C0.864 2.748 0 4.008 0 5.292C0 6.132 0.36 6.948 1.008 7.536C0.948 7.788 0.912 8.04 0.912 8.292C0.912 10.008 2.304 11.292 3.912 11.292C4.236 11.292 4.548 11.244 4.86 11.136C5.4 11.664 6.144 12 6.96 12Z" style="fill: var(--lfc-send-btn-fg)"/>' +
      '</g></svg></svg>',
  },
  {
    name: 'Gemini 3.5 Pro',
    iconSvg:
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">' +
      '<rect width="18" height="18" rx="4.5" style="fill: var(--lfc-send-btn-bg)"/>' +
      '<svg x="4" y="4" width="10" height="10" viewBox="0 0 753 768" preserveAspectRatio="none">' +
      '<path d="M752.64 392.727C752.64 365.498 750.196 339.316 745.658 314.182H384V462.895H590.662C581.585 510.72 554.356 551.215 513.513 578.444V675.142H638.138C710.749 608.116 752.64 509.673 752.64 392.727Z" fill="#4285F4"/>' +
      '<path d="M384 768C487.68 768 574.604 733.789 638.138 675.142L513.513 578.444C479.302 601.484 435.665 615.447 384 615.447C284.16 615.447 199.331 548.073 168.96 457.309H41.193V556.451C104.378 681.775 233.891 768 384 768Z" fill="#34A853"/>' +
      '<path d="M168.96 456.96C161.28 433.92 156.742 409.484 156.742 384C156.742 358.516 161.28 334.08 168.96 311.04V211.898H41.193C15.011 263.564 0 321.862 0 384C0 446.138 15.011 504.436 41.193 556.102L140.684 478.604L168.96 456.96Z" fill="#FBBC05"/>' +
      '<path d="M384 152.902C440.553 152.902 490.822 172.451 530.967 210.153L640.931 100.189C574.255 38.051 487.68 0 384 0C233.891 0 104.378 86.225 41.193 211.898L168.96 311.04C199.331 220.276 284.16 152.902 384 152.902Z" fill="#EA4335"/>' +
      '</svg></svg>',
  },
];

const CHAT_PROMPTS = [
  'Clearly summarize user feedback...',
  'Generate component from...',
  'Send test prompt to playground...',
];

// Standalone chat input row (sits between cards in col 1 and col 2)
function buildStandaloneChatRow() {
  const row = el('div', 'lfc-standalone-chat');
  const textEl = el('span', 'lfc-gradient-text', CHAT_PROMPTS[0]);
  const tab = el('span', 'lfc-tab-badge', 'TAB');
  const sendBtn = el('button', 'lfc-send-btn-sm');
  sendBtn.type = 'button';
  sendBtn.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M2.5 6L6 2.5M6 2.5L9.5 6M6 2.5V9.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  row.appendChild(textEl);
  row.appendChild(tab);
  row.appendChild(sendBtn);

  // Cycle through prompts: slide up out → swap → slide up in (Motion AnimatePresence pattern)
  let idx = 0;
  const EXIT_MS = 300;
  const ENTER_MS = 300;
  const HOLD_MS = 4000;
  const TRANSITION = `opacity ${EXIT_MS}ms ease, transform ${EXIT_MS}ms ease`;

  setInterval(() => {
    // Exit: slide up and fade out
    textEl.style.transition = TRANSITION;
    textEl.style.opacity = '0';
    textEl.style.transform = 'translateY(-10px)';

    setTimeout(() => {
      // Swap text, reset to below
      idx = (idx + 1) % CHAT_PROMPTS.length;
      textEl.textContent = CHAT_PROMPTS[idx];
      textEl.style.transition = 'none';
      textEl.style.opacity = '0';
      textEl.style.transform = 'translateY(10px)';

      // Trigger reflow so the reset takes hold before animating in
      void textEl.offsetHeight;

      // Enter: slide up and fade in
      textEl.style.transition = `opacity ${ENTER_MS}ms ease, transform ${ENTER_MS}ms ease`;
      textEl.style.opacity = '1';
      textEl.style.transform = 'translateY(0)';
    }, EXIT_MS);
  }, HOLD_MS + EXIT_MS);

  return row;
}

function buildPromptCard() {
  const card = el('div', 'lfc-card lfc-card--prompt');

  // Chat bubble icon
  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M10.833 6.667H5.833M14.167 10H5.833M17.5 12.5C17.5 12.942 17.324 13.366 17.012 13.678C16.699 13.991 16.275 14.167 15.833 14.167H5.833L2.5 17.5V4.167C2.5 3.725 2.676 3.301 2.988 2.988C3.301 2.676 3.725 2.5 4.167 2.5H15.833C16.275 2.5 16.699 2.676 17.012 2.988C17.324 3.301 17.5 3.725 17.5 4.167V12.5Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>',
      'Prompt',
    ),
  );

  // Text section label (no + button)
  const textHeader = el('div', 'lfc-section-header');
  textHeader.appendChild(el('span', null, 'Text'));
  card.appendChild(textHeader);

  // Prompt textarea box
  const promptBox = el('textarea', 'lfc-textarea');
  promptBox.placeholder = 'Add prompt...';
  card.appendChild(promptBox);

  return card;
}

function buildFileCard() {
  const card = el('div', 'lfc-card lfc-card--file');

  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.667 1.667V5C11.667 5.442 11.842 5.866 12.155 6.178C12.467 6.491 12.891 6.667 13.333 6.667H16.667M12.5 1.667H5C4.558 1.667 4.134 1.842 3.821 2.155C3.509 2.467 3.333 2.891 3.333 3.333V16.667C3.333 17.109 3.509 17.533 3.821 17.845C4.134 18.158 4.558 18.333 5 18.333H15C15.442 18.333 15.866 18.158 16.178 17.845C16.491 17.533 16.667 17.109 16.667 16.667V5.833L12.5 1.667Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'File',
    ),
  );

  // Google Drive area
  const driveArea = el('div', 'lfc-drive-area');
  driveArea.appendChild(el('div', null, 'Select files from Google Drive'));
  const driveBtn = el('button', 'lfc-drive-btn');
  driveBtn.type = 'button';
  driveBtn.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0a15.05 15.05 0 0 0 2 7.5z" fill="#0066da"/>' +
    '<path d="M43.65 25L29.9 1.2a9.24 9.24 0 0 0-3.3 3.3L2.25 43.5a14.91 14.91 0 0 0-2.25 7.5h27.5z" fill="#00ac47"/>' +
    '<path d="M73.55 76.8a9.24 9.24 0 0 0 3.3-3.3l1.6-2.75 7.65-13.25a14.91 14.91 0 0 0 2.25-7.5H60.85l5.85 11.5z" fill="#ea4335"/>' +
    '<path d="M43.65 25L57.4 1.2A14.85 14.85 0 0 0 50 0H37.3a14.85 14.85 0 0 0-7.4 1.2z" fill="#00832d"/>' +
    '<path d="M60.85 50H27.5L13.75 73.8a14.85 14.85 0 0 0 7.4 1.9h45a14.85 14.85 0 0 0 7.4-1.9z" fill="#2684fc"/>' +
    '<path d="M73.4 26.35l-13.5-23.4a9.24 9.24 0 0 0-2.5-1.75L43.65 25l17.2 25H87.3a14.91 14.91 0 0 0-2.25-7.5z" fill="#ffba00"/>' +
    '</svg> Add files';
  driveArea.appendChild(driveBtn);
  card.appendChild(driveArea);

  // Embedding model
  card.appendChild(
    formRow(
      'Embedding model',
      makeSelect([
        ['te3large', 'text-embedding-3-large', SVG_ICON_LANGFLOW],
        ['gemini', 'gemini-embedding-001', SVG_ICON_GOOGLE],
        ['cohere', 'embed-english-v3.0', SVG_ICON_COHERE],
      ]),
    ),
  );

  card.appendChild(
    formRow('Chunk size', makeNumberInput('1024', 'characters')),
  );
  card.appendChild(
    formRow('Chunk overlap', makeNumberInput('50', 'characters')),
  );

  return card;
}

function buildEmbeddingsCard() {
  const card = el('div', 'lfc-card lfc-card--embeddings');

  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 16.667H8.333M11.667 8.333H15M5 11.667H6.667V16.667M11.667 3.333H13.333V8.333M13.333 11.667C14.254 11.667 15 12.413 15 13.333V15C15 15.921 14.254 16.667 13.333 16.667C12.413 16.667 11.667 15.921 11.667 15V13.333C11.667 12.413 12.413 11.667 13.333 11.667ZM6.667 3.333C7.587 3.333 8.333 4.08 8.333 5V6.667C8.333 7.587 7.587 8.333 6.667 8.333C5.746 8.333 5 7.587 5 6.667V5C5 4.08 5.746 3.333 6.667 3.333Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'Embeddings',
    ),
  );

  card.appendChild(
    formRow(
      'Model',
      makeSelect([
        ['te3large', 'text-embedding-3-large', SVG_ICON_LANGFLOW],
        ['gemini', 'gemini-embedding-001', SVG_ICON_GOOGLE],
        ['cohere', 'embed-english-v3.0', SVG_ICON_COHERE],
      ]),
    ),
  );

  card.appendChild(
    formRow(
      'API Key',
      makeSelect([
        ['key1', 'co_1234567890abcdef...'],
        ['key2', 'Add new credential...'],
      ]),
    ),
  );

  return card;
}

function buildHistoryCard() {
  const card = el('div', 'lfc-card lfc-card--history');

  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 10C2.5 11.483 2.94 12.933 3.764 14.167C4.588 15.4 5.759 16.361 7.13 16.929C8.5 17.497 10.008 17.645 11.463 17.356C12.918 17.067 14.254 16.352 15.303 15.303C16.352 14.254 17.067 12.918 17.356 11.463C17.645 10.008 17.497 8.5 16.929 7.13C16.361 5.759 15.4 4.588 14.167 3.764C12.933 2.94 11.483 2.5 10 2.5C7.903 2.508 5.891 3.326 4.383 4.783L2.5 6.667M2.5 6.667V2.5M2.5 6.667H6.667M10 5.833V10L13.333 11.667" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'History',
    ),
  );

  const row = el('div', 'lfc-row');
  row.appendChild(el('span', 'lfc-label', 'Mode'));

  const toggleGroup = el('div', 'lfc-toggle-group');
  const btnRetrieve = el('button', 'lfc-toggle-btn active', 'Retrieve');
  btnRetrieve.type = 'button';
  const btnStore = el('button', 'lfc-toggle-btn', 'Store');
  btnStore.type = 'button';

  btnRetrieve.addEventListener('click', () => {
    btnRetrieve.classList.add('active');
    btnStore.classList.remove('active');
  });
  btnStore.addEventListener('click', () => {
    btnStore.classList.add('active');
    btnRetrieve.classList.remove('active');
  });

  toggleGroup.appendChild(btnRetrieve);
  toggleGroup.appendChild(btnStore);
  row.appendChild(toggleGroup);
  card.appendChild(row);

  return card;
}

function createWaveform() {
  const wrapper = document.createElement('div');
  wrapper.className = 'lfc-waveform';

  const canvas = document.createElement('canvas');
  wrapper.appendChild(canvas);

  const BAR_W = 3;
  const BAR_GAP = 2;
  const STEP = BAR_W + BAR_GAP;
  const HEIGHT = 48;

  let barParams = [];
  let lastNBars = 0;

  function initBars(n) {
    barParams = Array.from({ length: n }, () => ({
      p1: Math.random() * Math.PI * 2,
      p2: Math.random() * Math.PI * 2,
      p3: Math.random() * Math.PI * 2,
      f: 0.7 + Math.random() * 0.6,
    }));
  }

  function draw(ts) {
    const t = ts / 1000;
    const dpr = devicePixelRatio || 1;
    const W = canvas.offsetWidth;

    if (W > 0) {
      const nBars = Math.floor(W / STEP);
      if (nBars !== lastNBars) {
        canvas.width = W * dpr;
        canvas.height = HEIGHT * dpr;
        initBars(nBars);
        lastNBars = nBars;
      }

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const color =
        getComputedStyle(document.documentElement)
          .getPropertyValue('--lfc-range-thumb')
          .trim() || 'oklch(62% 0 0)';
      ctx.fillStyle = color;

      const cy = (HEIGHT * dpr) / 2;
      const maxH = cy * 0.85;

      for (let i = 0; i < nBars; i++) {
        const { p1, p2, p3, f } = barParams[i];
        const amp =
          0.4 * Math.sin(t * 1.9 * f + p1) +
          0.3 * Math.sin(t * 3.1 * f + p2) +
          0.3 * Math.sin(t * 5.7 * f + p3);
        const h = maxH * (0.12 + 0.88 * (amp * 0.5 + 0.5));
        const x = (i * STEP + BAR_W / 2) * dpr;
        const bw = BAR_W * dpr;
        const r = bw / 2;
        ctx.beginPath();
        ctx.roundRect(x - r, cy - h, bw, h * 2, r);
        ctx.fill();
      }
    }

    requestAnimationFrame(draw);
  }

  requestAnimationFrame(draw);
  return wrapper;
}

function buildListenCard() {
  const card = el('div', 'lfc-card lfc-card--listen');

  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 7.083C5 5.647 5.571 4.269 6.587 3.253C7.602 2.237 8.98 1.667 10.417 1.667C11.853 1.667 13.231 2.237 14.247 3.253C15.263 4.269 15.833 5.647 15.833 7.083C15.833 12.083 10.833 12.083 10.833 15.417C10.833 15.8 10.758 16.179 10.611 16.533C10.465 16.887 10.25 17.208 9.979 17.479C9.708 17.75 9.387 17.965 9.033 18.111C8.679 18.258 8.3 18.333 7.917 18.333C7.534 18.333 7.154 18.258 6.801 18.111C6.447 17.965 6.125 17.75 5.854 17.479C5.583 17.208 5.369 16.887 5.222 16.533C5.075 16.179 5 15.8 5 15.417M12.5 7.083C12.5 6.531 12.28 6.001 11.89 5.61C11.499 5.219 10.969 5 10.417 5C9.864 5 9.334 5.219 8.944 5.61C8.553 6.001 8.333 6.531 8.333 7.083V7.917C8.775 7.917 9.199 8.092 9.512 8.405C9.824 8.717 10 9.141 10 9.583C10 10.025 9.824 10.449 9.512 10.762C9.199 11.074 8.775 11.25 8.333 11.25" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'Listen',
    ),
  );

  card.appendChild(createWaveform());

  const select = makeSelect([
    ['', 'Add key...'],
    ['k1', 'user_input'],
    ['k2', 'system_prompt'],
  ]);
  select.querySelector('.lfc-select').style.color = 'var(--text-muted)';
  card.appendChild(formRow('Context Key', select));

  return card;
}

function buildGmailCard() {
  const card = el('div', 'lfc-card lfc-card--gmail');

  // Gmail icon is a standalone colored SVG — skip the #262626 wrap
  const gmailHeader = el('div', 'lfc-card-header');
  const gmailIconEl = el('div', 'lfc-card-icon-wrap lfc-card-icon-wrap--plain');
  gmailIconEl.innerHTML =
    '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M0 6C0 2.686 2.686 0 6 0H26C29.314 0 32 2.686 32 6V26C32 29.314 29.314 32 26 32H6C2.686 32 0 29.314 0 26V6Z" fill="#FFFFFF"/>' +
    '<path d="M23.04 16.167C23.04 15.647 22.993 15.147 22.907 14.667H16V17.507H19.947C19.773 18.42 19.253 19.193 18.473 19.713V21.56H20.853C22.24 20.28 23.04 18.4 23.04 16.167Z" fill="#4285F4"/>' +
    '<path d="M16 23.333C17.98 23.333 19.64 22.68 20.853 21.56L18.473 19.713C17.82 20.153 16.987 20.42 16 20.42C14.093 20.42 12.473 19.133 11.893 17.4H9.453V19.293C10.66 21.687 13.133 23.333 16 23.333Z" fill="#34A853"/>' +
    '<path d="M11.893 17.393C11.747 16.953 11.66 16.487 11.66 16C11.66 15.513 11.747 15.047 11.893 14.607V12.713H9.453C8.953 13.7 8.667 14.813 8.667 16C8.667 17.187 8.953 18.3 9.453 19.287L11.353 17.807L11.893 17.393Z" fill="#FBBC05"/>' +
    '<path d="M16 11.587C17.08 11.587 18.04 11.96 18.807 12.68L20.907 10.58C19.633 9.393 17.98 8.667 16 8.667C13.133 8.667 10.66 10.313 9.453 12.713L11.893 14.607C12.473 12.873 14.093 11.587 16 11.587Z" fill="#EA4335"/>' +
    '</svg>';
  const gmailActions = el('div', 'lfc-card-header-actions');
  const gmailCodeBtn = el('button', 'lfc-btn-icon');
  gmailCodeBtn.type = 'button';
  gmailCodeBtn.innerHTML = SVG_CODE;
  const gmailDotsBtn = el('button', 'lfc-btn-icon');
  gmailDotsBtn.type = 'button';
  gmailDotsBtn.innerHTML = SVG_DOTS;
  gmailActions.appendChild(gmailCodeBtn);
  gmailActions.appendChild(gmailDotsBtn);
  gmailHeader.appendChild(gmailIconEl);
  gmailHeader.appendChild(el('div', 'lfc-card-title', 'Gmail'));
  gmailHeader.appendChild(gmailActions);
  card.appendChild(gmailHeader);

  card.appendChild(
    formRow(
      'API Key',
      makeSelect([
        ['key1', 'co_1234567890abcdef...'],
        ['key2', 'Add new credential...'],
      ]),
    ),
  );

  const toolsHeader = el('div', 'lfc-section-header');
  toolsHeader.appendChild(el('span', null, 'Tools'));
  const toolsAddBtn = el('button', 'lfc-btn-icon');
  toolsAddBtn.type = 'button';
  toolsAddBtn.innerHTML = SVG_PLUS;
  toolsHeader.appendChild(toolsAddBtn);
  card.appendChild(toolsHeader);

  ['GMAIL_GET_PROFILE', 'GMAIL_SEND_EMAIL', 'GMAIL_REPLY_TO_THREAD'].forEach(
    (name) => {
      const row = el('div', 'lfc-tool-row');
      row.appendChild(el('span', 'lfc-tool-name', name));
      const dotsBtn = el('button', 'lfc-btn-icon');
      dotsBtn.type = 'button';
      dotsBtn.innerHTML = SVG_DOTS;
      row.appendChild(dotsBtn);
      card.appendChild(row);
    },
  );

  const recipientInput = makeInput('Add recipient...');
  card.appendChild(formRow('Recipient', recipientInput));

  const subjectInput = makeInput('Add subject...');
  card.appendChild(formRow('Subject', subjectInput));

  return card;
}

function buildSplitCard() {
  const card = el('div', 'lfc-card lfc-card--split');

  card.appendChild(
    cardHeaderFull(
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.517 7.85L6.667 10M11.667 5L4.517 12.15M9 12.333L11.667 15M13.333 10H11.667M18.333 10H16.667M5 6.667C5 7.587 4.254 8.333 3.333 8.333C2.413 8.333 1.667 7.587 1.667 6.667C1.667 5.746 2.413 5 3.333 5C4.254 5 5 5.746 5 6.667ZM5 13.333C5 14.254 4.254 15 3.333 15C2.413 15 1.667 14.254 1.667 13.333C1.667 12.413 2.413 11.667 3.333 11.667C4.254 11.667 5 12.413 5 13.333Z" stroke="#737373" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      'Split',
    ),
  );

  card.appendChild(rangeRow('Overlap', '0', '500', '200'));
  card.appendChild(rangeRow('Size', '0', '2000', '1000'));

  const sepInput = makeInput('Add separator...');
  card.appendChild(formRow('Separator', sepInput));

  return card;
}

// ---------------------------------------------------------------------------
// Collage assembly
// ---------------------------------------------------------------------------

function buildCollage(panel) {
  const collage = el('div', 'lfc-collage');

  // Column 1: Agent + chat (tight group), Prompt
  const col1 = el('div', 'lfc-col');
  const agentGroup = document.createElement('div');
  agentGroup.style.cssText = 'display:flex;flex-direction:column;gap:8px;';
  agentGroup.appendChild(buildAgentCard());
  agentGroup.appendChild(buildStandaloneChatRow());
  col1.appendChild(agentGroup);
  col1.appendChild(buildPromptCard());
  collage.appendChild(col1);

  // Column 2: Listen, Gmail, Split
  const col2 = el('div', 'lfc-col');
  col2.appendChild(buildListenCard());
  col2.appendChild(buildGmailCard());
  col2.appendChild(buildSplitCard());
  collage.appendChild(col2);

  // Column 3: File, Embeddings, History
  const col3 = el('div', 'lfc-col');
  col3.appendChild(buildFileCard());
  col3.appendChild(buildEmbeddingsCard());
  col3.appendChild(buildHistoryCard());
  collage.appendChild(col3);

  panel.appendChild(collage);
}
